import { useCallback, useEffect, useMemo, useState } from 'react';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { NoSymbolIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';

import { Button, Combobox, TextInput } from '@restworld/ui-ds-base';
import { useAnalytics } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';
import { JobApplication } from '@restworld/utility-types';

import useUpdateJobApplication from '../../../../hooks/use-update-job-application';
import { useEmployerJobApplicationQueryKey } from '../../../../hooks/use-employer-job-applications';
import { useDiscardReasonsForJobApplication } from '../../../../hooks/use-discard-reasons';
import {
  candidateModalVariantAtom,
  isCandidateModalSuccessScreenAtom,
  matchReviewConsentDataAtom,
} from '../../../../employer-job-listing.atoms';
import CandidateStateUpdateSuccess from '../candidate-state-update-success';

interface UseDiscardCandidateOpts {
  firstName: string;
  jobPositionId: string;
  workerId: string;
  jobApplication?: Partial<JobApplication>;
  onClose: () => void;
}

interface DiscardFormValues {
  reasonLabel?: string;
  otherDetails?: string;
}

/**
 * Owns the discard-candidate flow state and returns the body + footer JSX so
 * the parent (candidate-modal) can mount each into the corresponding BaseModal
 * slot.
 *
 * Flow:
 *   INPUT_REASON → submit finalizes the discard
 *     ↳ if reason triggers match-review: opens the consent prompt (then on
 *       accept, the standalone MatchReviewModal) and closes this one.
 *     ↳ otherwise: SUCCESS screen inside this modal.
 */
export function useDiscardCandidate({
  firstName,
  jobPositionId,
  workerId,
  jobApplication,
  onClose,
}: UseDiscardCandidateOpts) {
  const { t } = useTranslation('job-positions-page');
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();
  const isUsingPPU = user?.using_ppu_business_model === true;
  const { control, handleSubmit, watch } = useForm<DiscardFormValues>();
  const [isSuccess, setIsSuccess] = useState(false);
  const variant = useAtomValue(candidateModalVariantAtom);
  const setCandidateModalVariant = useSetAtom(candidateModalVariantAtom);
  const setIsCandidateModalSuccessScreen = useSetAtom(isCandidateModalSuccessScreenAtom);
  const setMatchReviewConsentData = useSetAtom(matchReviewConsentDataAtom);

  // The hook is mounted by the always-rendered CandidateModal, so its useState
  // outlives any single discard. Reset whenever the modal leaves the discard
  // variant (close, cancel, switch candidate) so the next discard starts on
  // the form, not the previous candidate's success screen.
  useEffect(() => {
    if (variant !== 'worker_jp_discarded_confirmation') setIsSuccess(false);
  }, [variant]);

  const { mutate, isPending } = useUpdateJobApplication();
  const queryClient = useQueryClient();
  const employerJobApplicationQueryKey = useEmployerJobApplicationQueryKey();

  const jobApplicationId = workerId && jobPositionId ? `${workerId}${jobPositionId}` : undefined;
  const matchBreakdown = jobApplication?.evaluation?.match_breakdown ?? [];
  const visibleBreakdown = useMemo(() => matchBreakdown.filter((it) => it.match_quality !== 'weak'), [matchBreakdown]);

  const { data: discardReasons = [], isLoading } = useDiscardReasonsForJobApplication(jobApplicationId);
  const labels = useMemo(() => discardReasons.map((r) => r.label), [discardReasons]);
  const watchedLabel = watch('reasonLabel');
  const canSubmit = !!watchedLabel;
  const selectedReason = useMemo(
    () => discardReasons.find((r) => r.label === watchedLabel),
    [discardReasons, watchedLabel]
  );
  const corpusMarkdown = jobApplication?.evaluation?.job_offer_corpus_markdown;
  // Gated to PPU clients: the educational match-review modal is part of the
  // PPU value loop (refine the ad → next candidates align better → fewer
  // wasted PPU days). Non-PPU employers are out of scope for now.
  const matchReviewApplicable =
    isUsingPPU && !!selectedReason?.triggers_match_review && visibleBreakdown.length > 0 && !!corpusMarkdown;

  const goBackToCandidateView = useCallback(() => {
    setCandidateModalVariant('default');
  }, [setCandidateModalVariant]);

  const onSubmit: SubmitHandler<DiscardFormValues> = useCallback(
    (values) => {
      const reason = discardReasons.find((r) => r.label === values.reasonLabel);
      if (!reason) return;

      mutate(
        {
          params: {
            discarded_reason: reason.id,
            discarded_other_reason: values.otherDetails,
            discarded_by: 'employer',
            status_key: 'worker_jp_discarded',
          },
          workerId,
          jobPositionId,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: employerJobApplicationQueryKey });

            // Submit-level event so we can compute the discard → match-review
            // funnel in PostHog. `match_review_eligible` separates "would have
            // shown the consent prompt" from "actually shown" (which depends
            // on the corpus being available too).
            trackEvent('employer_discard_submitted', {
              jp_id: jobPositionId,
              worker_id: workerId,
              reason_key: reason.key,
              recommended: jobApplication?.recommended === true,
              match_review_eligible: matchReviewApplicable,
              breakdown_size: visibleBreakdown.length,
            });

            if (matchReviewApplicable && corpusMarkdown) {
              // Hand off to the standalone consent prompt, then close this one.
              // The discard mutation already finalized — the consent prompt is
              // independent and optional, and forwards to the match-review
              // modal only on accept.
              setMatchReviewConsentData({
                firstName,
                jobPositionId,
                jobOfferCorpusMarkdown: corpusMarkdown,
                matchBreakdown,
              });
              onClose();
            } else {
              // Flip the candidate-modal-level atom so candidate-modal hides
              // the desktop HeaderWrapper / mobile title / header border that
              // would otherwise sit on top of the success screen.
              setIsCandidateModalSuccessScreen(true);
              setIsSuccess(true);
            }
          },
        }
      );
    },
    [
      discardReasons,
      mutate,
      workerId,
      jobPositionId,
      queryClient,
      employerJobApplicationQueryKey,
      matchReviewApplicable,
      corpusMarkdown,
      matchBreakdown,
      firstName,
      onClose,
      setIsCandidateModalSuccessScreen,
      setMatchReviewConsentData,
      trackEvent,
      visibleBreakdown.length,
      jobApplication?.recommended,
    ]
  );

  const submitForm = handleSubmit(onSubmit);

  const body = isSuccess ? (
    <BodyWrapper>
      <CandidateStateUpdateSuccess onClose={onClose} isDiscarded />
    </BodyWrapper>
  ) : (
    <BodyWrapper>
      <Header>
        <Title>{t('discard.title', { name: firstName })}</Title>
        <Subtitle>{t('discard.subtitle')}</Subtitle>
      </Header>

      <Inputs>
        <Field>
          <FieldLabel htmlFor="reason">{t('reason')}</FieldLabel>
          <Controller
            name="reasonLabel"
            control={control}
            render={({ field }) => (
              <Combobox
                dropdownList={labels}
                placeholder={isLoading ? t('loading') : t('select_the_reason')}
                inputWrapperStyle={tw`w-full`}
                fullwidth
                {...field}
              />
            )}
          />
        </Field>

        <Field>
          <FieldLabelRow>
            <FieldLabel htmlFor="otherDetails">{t('other_details')}</FieldLabel>
            <OptionalLabel>{`(${t('optional')})`}</OptionalLabel>
          </FieldLabelRow>
          <Controller
            name="otherDetails"
            control={control}
            render={({ field }) => (
              <TextInput id="other-details-input" type="text" bordercolor="neutral-100" isFullwidth {...field} />
            )}
          />
        </Field>
      </Inputs>
    </BodyWrapper>
  );

  const footer = isSuccess ? null : (
    <FooterRow>
      <Button type="button" variant="tertiary" onClick={goBackToCandidateView}>
        {t('cancel')}
      </Button>
      <Button
        type="button"
        disabled={!canSubmit}
        loading={isPending}
        variant="secondary-danger"
        StartIcon={NoSymbolIcon}
        onClick={submitForm}
      >
        {t('discard_and_send_feedback')}
      </Button>
    </FooterRow>
  );

  return { body, footer };
}

const BodyWrapper = styled.div`
  ${tw`max-w-screen-sm mx-auto px-4 py-6 text-left md:(py-10 px-12)`}
`;
const Header = styled.div`
  ${tw`mb-8 text-center`}
`;
const Title = styled.h2`
  ${tw`text-xl lg:text-2xl font-semibold text-neutral-700 mb-2`}
`;
const Subtitle = styled.p`
  ${tw`text-base text-neutral-200 leading-relaxed`}
`;
const Inputs = styled.div`
  ${tw`flex flex-col gap-6 w-full`}
`;
const Field = styled.div`
  ${tw`flex flex-col gap-2`}
`;
const FieldLabel = styled.label`
  ${tw`font-medium text-neutral-700 text-base`}
`;
const FieldLabelRow = styled.div`
  ${tw`flex items-baseline justify-between`}
`;
const OptionalLabel = styled.span`
  ${tw`text-neutral-100 text-sm`}
`;
// Mirrors the default candidate-modal footer container so the modal stays
// visually aligned across variants. BaseModal is mounted with `noPadding` from
// the candidate-modal, so DesktopModalFooter's default py-4 is killed and we
// need to re-add vertical padding here.
const FooterRow = styled.div`
  ${tw`flex flex-col-reverse items-stretch justify-between w-full gap-3 px-4 py-3 lg:(flex-row items-center px-10 py-4)`}
`;
